/* eslint-disable react/display-name */
import React, { forwardRef, memo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import ListSubheader from '@material-ui/core/ListSubheader';
import { areEqual, FixedSizeList } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import { ListItem } from '@material-ui/core';
import { DROPDOWN_ITEMS_MAX } from '.';

const LISTBOX_PADDING = 8;

const OuterElementContext = React.createContext({});

const Element = (props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
};

const OuterElementType = forwardRef(Element);

const renderRow = (props, isItemLoaded) => {
  const { data, index, style } = props;
  if (!isItemLoaded(index)) {
    return (
      <ListItem
        style={{
          ...style,
          top: style.top + LISTBOX_PADDING,
        }}
      >
        Loading...
      </ListItem>
    );
  }

  if (!data[index]) {
    return null;
  }

  return React.cloneElement(data[index], {
    style: {
      ...style,
      top: style.top + LISTBOX_PADDING,
    },
  });
};

const ListboxComponent = forwardRef((props, ref) => {
  const {
    children,
    isItemLoaded,
    loadMoreItems,
    itemCount,
    scrollToIndex = 0,
    ...other
  } = props;
  const itemData = React.Children.toArray(children);
  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true });
  const itemSize = smUp ? 36 : 20;
  const virtualLoaderRef = useRef(null);

  const getChildSize = child => {
    if (React.isValidElement(child) && child.type === ListSubheader) {
      return 48;
    }

    return itemSize;
  };

  useEffect(() => {
    if (virtualLoaderRef.current) {
      virtualLoaderRef.current.scrollToItem(scrollToIndex, 'center');
    }
  }, [scrollToIndex]);

  const getHeight = () => {
    if (itemCount > DROPDOWN_ITEMS_MAX) {
      return DROPDOWN_ITEMS_MAX * itemSize;
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
  };

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <InfiniteLoader
          isItemLoaded={isItemLoaded}
          itemCount={itemCount}
          loadMoreItems={loadMoreItems}
        >
          {({ onItemsRendered, ref: refList }) => {
            return (
              <FixedSizeList
                useIsScrolling
                ref={list => {
                  refList(list);
                  virtualLoaderRef.current = list;
                }}
                itemData={itemData}
                height={getHeight() + 2 * LISTBOX_PADDING}
                width="100%"
                outerElementType={OuterElementType}
                innerElementType="ul"
                itemSize={itemSize}
                overscanCount={5}
                itemCount={itemCount}
                onItemsRendered={onItemsRendered}
              >
                {memo(props => renderRow(props, isItemLoaded), areEqual)}
              </FixedSizeList>
            );
          }}
        </InfiniteLoader>
      </OuterElementContext.Provider>
    </div>
  );
});

export default ListboxComponent;

ListboxComponent.propTypes = {
  children: PropTypes.array,
  isItemLoaded: PropTypes.func,
  loadMoreItems: PropTypes.func,
  itemCount: PropTypes.number,
  isLoadingMore: PropTypes.bool,
  scrollToIndex: PropTypes.number,
};
